import { HttpApiService } from "@/shared/services/httpApi.service";

import type {
  ClearRailSettingScope,
  RailSettingsResponse,
  UpsertRailSettingPayload,
} from "../types/paymentRails.types";

/**
 * Rail availability kill-switch — coloca-nest `InternalPaymentRailsController`. Every endpoint is `userTypes:
 * [INTERNAL]`:
 *
 * - `GET /internal/payments/rails?organization_id=` (`payments:read`) — the full rail × direction matrix.
 * - `PUT /internal/payments/rails/:rail` (`payments:write`) — atomic upsert of one exact scope; the backend
 *   requires `reason` when disabling and stamps the author. Superseded rows stay as the audit trail.
 * - `DELETE /internal/payments/rails/:rail?direction=&organization_id=` (`payments:write`) — removes one exact
 *   scope so it follows the wider one (or the enabled-by-default) again; 404 `rail_setting_not_found` when no
 *   live row matches.
 *
 * A flip takes effect immediately on the serving instance and within 60 s on the rest; the corridor catalog
 * (`useAvailableCorridors`) reflects it as `unavailable_reason: "rail_disabled"`.
 */

const RAILS_URL = "/internal/payments/rails";

const toScopeQuery = (scope: ClearRailSettingScope): string => {
  const params = new URLSearchParams();
  if (scope.direction) params.set("direction", scope.direction);
  if (scope.organization_id) params.set("organization_id", scope.organization_id);
  const query = params.toString();
  return query ? `?${query}` : "";
};

export const PaymentRailsService = {
  async list(organizationId?: string | null): Promise<RailSettingsResponse> {
    const query = organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : "";
    const { data } = await HttpApiService.get<RailSettingsResponse>(`${RAILS_URL}${query}`);
    return data;
  },

  async upsert(rail: string, payload: UpsertRailSettingPayload): Promise<void> {
    await HttpApiService.put(`${RAILS_URL}/${encodeURIComponent(rail)}`, payload);
  },

  async clear(rail: string, scope: ClearRailSettingScope): Promise<void> {
    await HttpApiService.delete(`${RAILS_URL}/${encodeURIComponent(rail)}${toScopeQuery(scope)}`);
  },
};
